import { parseISO } from 'date-fns';
import { useCallback, useRef, useState } from 'react';
import { OrderManagementResource } from '../../resources';
import {
  CurrencyToBalanceMapping,
  ExchangeRate,
  ListedInstrument,
  OrderExchangeFundingData,
  OrderExchangeFundingMode,
  SelectedAccount,
  SettlementDateWrapper,
} from '../../types';
import { getCorrectExchangeRate, getDominantCurrency, isDefined, Loadable, LoadableState } from '../../utils';
import { GetResourceHeaderWithTokenForAccount } from '../useResource';

type UseOrderExchangeExchangeRateCommonParams = {
  funding: LoadableState<OrderExchangeFundingData>;
  fundingMode: OrderExchangeFundingMode | undefined;
  instrument: ListedInstrument | undefined | null;
  sourceCurrency: string | undefined;
  selectedAccount: SelectedAccount | undefined;
  balancesByCurrency: LoadableState<CurrencyToBalanceMapping>;
};

export type UseOrderExchangeExchangeRateCommonPlatformParams = {
  orderManagementResource: OrderManagementResource;
  getResourceHeaderWithTokenForAccount: GetResourceHeaderWithTokenForAccount;
};

type UseOrderExchangeExchangeRateOutput = {
  loadExchangeRate: () => Promise<void>;
  exchangeRate: LoadableState<ExchangeRate>;
  settlementDate: LoadableState<SettlementDateWrapper>;
  reset: () => void;
};

type UpdateParams = {
  requestId: number;
  exchangeRate: LoadableState<ExchangeRate>;
  settlementDate: LoadableState<SettlementDateWrapper>;
};

export const useOrderExchangeExchangeRateCommon = (
  {
    funding,
    fundingMode,
    instrument,
    sourceCurrency,
    selectedAccount,
    balancesByCurrency,
  }: UseOrderExchangeExchangeRateCommonParams,
  { orderManagementResource, getResourceHeaderWithTokenForAccount }: UseOrderExchangeExchangeRateCommonPlatformParams,
): UseOrderExchangeExchangeRateOutput => {
  const [exchangeRate, setExchangeRate] = useState<LoadableState<ExchangeRate>>(Loadable.uninitialized);
  const [settlementDate, setSettlementDate] = useState<LoadableState<SettlementDateWrapper>>(Loadable.uninitialized);

  const requestIdRef = useRef(0);

  const update = useCallback(({ requestId, exchangeRate, settlementDate }: UpdateParams): void => {
    if (requestId === requestIdRef.current) {
      setExchangeRate(exchangeRate);
      setSettlementDate(settlementDate);
    }
  }, []);

  const loadExchangeRate = useCallback(async (): Promise<void> => {
    const requestId = ++requestIdRef.current;
    if (
      !Loadable.hasValue(funding) ||
      !Loadable.hasValue(balancesByCurrency) ||
      !isDefined(sourceCurrency) ||
      !isDefined(fundingMode) ||
      !isDefined(selectedAccount?.account?.accountNumber) ||
      !isDefined(instrument?.mic) ||
      !isDefined(balancesByCurrency[sourceCurrency])
    ) {
      update({ requestId, exchangeRate: Loadable.uninitialized, settlementDate: Loadable.uninitialized });
      return;
    }

    const balance = balancesByCurrency[sourceCurrency];

    // We have a selected source currency, but it's not in balances anymore => it cannot be exchanged.
    if (!isDefined(balance)) {
      update({ requestId, exchangeRate: Loadable.uninitialized, settlementDate: Loadable.uninitialized });
      return;
    }

    update({ requestId, exchangeRate: Loadable.loading, settlementDate: Loadable.loading });
    try {
      const [{ data }, { data: settlementDateData }] = await Promise.all([
        orderManagementResource.getExchangeRateForOrder(
          {
            sourceCcyCode: sourceCurrency,
            targetCcyCode: funding.targetCurrency,
            targetAmount: fundingMode === 'fullAmount' ? funding.remaining.total : funding.missingAmount,
          },
          getResourceHeaderWithTokenForAccount(selectedAccount.account.accountNumber),
        ),
        orderManagementResource.getExchangeSettlementDate(
          { mic: instrument.mic },
          getResourceHeaderWithTokenForAccount(selectedAccount.account.accountNumber),
        ),
      ]);

      const newExchangeRate: ExchangeRate = {
        rawExchangeRate: data.exchangeRate,
        exchangeRate: getCorrectExchangeRate({
          currencyPair: data.currencyPair,
          exchangeRate: data.exchangeRate,
          sourceCurrency,
        }),
        dominantCurrency: getDominantCurrency({
          currencyPair: data.currencyPair,
          sourceCurrency,
          targetCurrency: funding.targetCurrency,
        }),
        validFrom: new Date(),
        validUntil: isDefined(data.validTo) ? parseISO(data.validTo) : 'indefinite',
        sourceCurrency,
        targetCurrency: funding.targetCurrency,
      };

      update({
        requestId,
        exchangeRate: Loadable.loaded(newExchangeRate),
        settlementDate: Loadable.loaded({ date: parseISO(settlementDateData.settlementDate) }),
      });
    } catch (e) {
      update({ requestId, exchangeRate: Loadable.failed, settlementDate: Loadable.failed });
    }
  }, [
    balancesByCurrency,
    funding,
    fundingMode,
    getResourceHeaderWithTokenForAccount,
    instrument?.mic,
    orderManagementResource,
    selectedAccount?.account?.accountNumber,
    sourceCurrency,
    update,
  ]);

  const reset = useCallback((): void => {
    ++requestIdRef.current;
    setExchangeRate(Loadable.uninitialized);
    setSettlementDate(Loadable.uninitialized);
  }, []);

  return { loadExchangeRate, exchangeRate, settlementDate, reset };
};
